import React, { useState, useEffect, useContext, useRef, useMemo, useCallback } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import '../styles/components/BottomNavigation.css';
import { GraphContext } from '../contexts/GraphContext';
import { useAuth } from '../contexts/AuthContext';
import { LocalTimeContext } from '../contexts/LocalTimeContext';
import { InfoButtonContext } from '../contexts/InfoButtonContext';
import { useLiquidGlass } from '../contexts/LiquidGlassContext';
import { WDWContext } from '../contexts/WDWContext';
import WeatherModal from './WeatherModal';
import ParkingAssistant from './ParkingAssistant';
import LiquidToggle from './LiquidToggle';
import ThemeToggle from './ThemeToggle';
import { isMobile } from '../utils/deviceDetection';
import { getParkCoordinates, isParkWeatherSupported } from '../config';
import { getRainSummaryFromHourly } from '../utils/weatherRainUtils';
import useSharedParkData from '../hooks/useSharedParkData';
import {
  applyCustomOrder,
  saveCustomParkOrder,
  resetParkOrder,
  hasCustomOrder,
  moveParkUp,
  moveParkDown
} from '../utils/parkOrderManager';
import { ArrowUp, ArrowDown, Star, Snowflake, Sun, Pencil, Trash2, Users, UserCheck } from 'lucide-react';
import { onAuthEvent, AUTH_EVENTS } from '../utils/userPreferencesCookie';
import { findGenericThemeparksContentPark } from '../utils/themeparksGenericContentConfig';
import { useThemeparksContentAvailability } from '../utils/themeparksContentAvailability';
import { CROWD_PREDICTION_PARK_PATHS } from '../constants/crowdPredictionParkPaths';
// Bundles de traductions Accessibilité
import accessibilityFr from '../locales/accessibility/fr.json';
import accessibilityEn from '../locales/accessibility/en.json';
import accessibilityDe from '../locales/accessibility/de.json';
import accessibilityEs from '../locales/accessibility/es.json';
import accessibilityIt from '../locales/accessibility/it.json';

const BOTTOM_NAV_WEATHER_CACHE = new Map();
const BOTTOM_NAV_WEATHER_INFLIGHT = new Map();
const BOTTOM_NAV_WEATHER_TTL = 60 * 1000;

const BottomNavigation = ({
  isDarkMode,
  toggleTheme,
  isHighContrast,
  toggleHighContrast,
  currentLanguage,
  isLanguageMenuOpen,
  setIsLanguageMenuOpen,
  changeLanguage,
  showSnow,
  toggleSnow,
  showIOSRefresh
}) => {
  const [isVisible, setIsVisible] = useState(true);
  const isVisibleRef = useRef(true);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isMenuClosing, setIsMenuClosing] = useState(false);
  const [selectedContinent, setSelectedContinent] = useState('europe');
  const [showWeatherModal, setShowWeatherModal] = useState(false);
  const [isWeatherClosing, setIsWeatherClosing] = useState(false);
  const [showParkingModal, setShowParkingModal] = useState(false);
  const [hideNavLabels, setHideNavLabels] = useState(() => localStorage.getItem('hideNavLabels') === 'true');

  // Écouter les événements d'authentification pour réinitialiser/restaurer hideNavLabels
  useEffect(() => {
    const handleSignedOut = () => {
      setHideNavLabels(false);
    };
    const handleSignedIn = () => {
      const saved = localStorage.getItem('hideNavLabels');
      if (saved !== null) {
        setHideNavLabels(saved === 'true');
      }
    };
    const cleanupOut = onAuthEvent(AUTH_EVENTS.SIGNED_OUT, handleSignedOut);
    const cleanupIn = onAuthEvent(AUTH_EVENTS.SIGNED_IN, handleSignedIn);
    return () => { cleanupOut(); cleanupIn(); };
  }, []);
  const [excludedFavoriteParkIds, setExcludedFavoriteParkIds] = useState(() => {
    try {
      const raw = localStorage.getItem('excludedFavoriteParkIds');
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  });
  const [hasRainAlert, setHasRainAlert] = useState(false);
  const [isReorderMode, setIsReorderMode] = useState(false);
  const [tempParks, setTempParks] = useState([]);
  const [isChrome, setIsChrome] = useState(false);
  const [bubbleStyle, setBubbleStyle] = useState({ left: 0, width: 0, height: 0, top: 0 });
  const [activeToggle, setActiveToggle] = useState(null);
  const { favorites: attractionFavorites, favoriteParkIds, toggleParkFavorite } = useSharedParkData();
  const navRef = useRef(null);
  const lastNavHapticAtRef = useRef(0);
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const { user, profile } = useAuth();
  const genericThemeparksContentPark = findGenericThemeparksContentPark(location.pathname);
  const genericThemeparksContentAvailability = useThemeparksContentAvailability(
    genericThemeparksContentPark?.parkSlug || null,
    genericThemeparksContentPark
  );
  const { setGraphEnabled } = useContext(GraphContext);
  // Contextes conservés pour usage futur
  // eslint-disable-next-line no-unused-vars
  const { showInfoButtons, toggleInfoButtons } = useContext(InfoButtonContext);
  // eslint-disable-next-line no-unused-vars
  const { showWDW, toggleWDW } = useContext(WDWContext);
  const { showLocalTime, toggleLocalTime } = useContext(LocalTimeContext);
  const { liquidGlassEnabled, toggleLiquidGlass } = useLiquidGlass();

  const isVipOrAdmin = profile?.role === 'vip' || profile?.role === 'admin';
  const showCrowdPredictionsOption = (() => {
    try {
      return localStorage.getItem('showCrowdPredictions') === 'true';
    } catch {
      return false;
    }
  })();
  const shouldShowCrowdPredictionIndicator = Boolean(user) && isVipOrAdmin && showCrowdPredictionsOption;

  // Léger retour haptique lors des actions de navigation (mobile uniquement)
  const triggerNavHaptic = () => {
    if (typeof navigator === 'undefined' || typeof navigator.vibrate !== 'function') return;
    const now = Date.now();
    // Anti-spam si l'utilisateur clique vite
    if (now - lastNavHapticAtRef.current < 250) return;
    lastNavHapticAtRef.current = now;
    navigator.vibrate(12);
  };

  const effectiveLanguage = (currentLanguage || i18n.language || 'en').split('-')[0];

  const shouldShowSnowButton = () => {
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const startDate = new Date(currentYear, 10, 8); // 8 novembre
    const endDate = new Date(currentYear + 1, 0, 6); // 6 janvier année suivante

    return currentDate >= startDate && currentDate <= endDate;
  };

  const normalizeParkIdForMenu = (rawId) => {
    switch (rawId) {
      case 'DLP':
        return 'dlp';
      case 'WDS':
        return 'wds';
      case '85e3b542-af91-4f8a-8d28-445868a7c8fd':
        return 'ep';
      case 'efteling':
        return 'eft';
      case 'asterix':
        return 'ast';
      case 'nigloland':
        return 'niglo';
      case 'busch-gardens-tampa':
        return 'buschgardens';
      case 'legoland-florida':
        return 'legoland';
      case 'seaworld-orlando':
        return 'seaworld';
      default:
        return rawId;
    }
  };

  const favoriteParkIdsSet = useMemo(() => {
    const ids = new Set(
      Array.isArray(favoriteParkIds)
        ? favoriteParkIds.map((id) => normalizeParkIdForMenu(id))
        : []
    );

    if (Array.isArray(attractionFavorites)) {
      attractionFavorites.forEach((key) => {
        if (typeof key === 'string') {
          const [parkId] = key.split('_');
          if (parkId) {
            ids.add(normalizeParkIdForMenu(parkId));
          }
        }
      });
    }

    return ids;
  }, [attractionFavorites, favoriteParkIds]);

  const favoriteParkIdsOnlySet = useMemo(() => {
    return new Set(
      Array.isArray(favoriteParkIds)
        ? favoriteParkIds.map((id) => normalizeParkIdForMenu(id))
        : []
    );
  }, [favoriteParkIds]);

  const excludedFavoriteParkIdsSet = useMemo(() => {
    return new Set(Array.isArray(excludedFavoriteParkIds) ? excludedFavoriteParkIds : []);
  }, [excludedFavoriteParkIds]);

  const visibleFavoriteParkIdsSet = useMemo(() => {
    const ids = new Set();
    favoriteParkIdsSet.forEach((id) => {
      if (!excludedFavoriteParkIdsSet.has(id)) ids.add(id);
    });
    return ids;
  }, [favoriteParkIdsSet, excludedFavoriteParkIdsSet]);

  const favoriteParkRawIdByNormalized = useMemo(() => {
    const map = new Map();
    if (Array.isArray(favoriteParkIds)) {
      favoriteParkIds.forEach((rawId) => {
        const normalizedId = normalizeParkIdForMenu(rawId);
        if (normalizedId && !map.has(normalizedId)) {
          map.set(normalizedId, rawId);
        }
      });
    }
    return map;
  }, [favoriteParkIds]);

  const hasFavoriteParks = visibleFavoriteParkIdsSet.size > 0;

  useEffect(() => {
    try {
      localStorage.setItem('excludedFavoriteParkIds', JSON.stringify(excludedFavoriteParkIds));
    } catch {
      // noop
    }
  }, [excludedFavoriteParkIds]);

  const excludeFavoritePark = (parkId) => {
    if (!parkId) return;
    setExcludedFavoriteParkIds((prev) => {
      if (!Array.isArray(prev)) return [parkId];
      if (prev.includes(parkId)) return prev;
      return [...prev, parkId];
    });
  };

  // Fonction pour vérifier si Traumatica est actif selon les dates Halloween (24.09.2025 - 08.11.2025)
  const isTraumaticaActive = () => {
    const now = new Date();
    const currentYear = now.getFullYear();

    // Pour l'année 2025, vérifier les dates spécifiées
    if (currentYear === 2025) {
      const startDate = new Date('2025-09-24');
      const endDate = new Date('2025-11-08');
      return now >= startDate && now <= endDate;
    }

    // Pour les autres années, utiliser une logique par défaut (octobre)
    if (currentYear > 2025) {
      const startDate = new Date(currentYear, 9, 15); // 15 octobre
      const endDate = new Date(currentYear, 10, 15); // 15 novembre
      return now >= startDate && now <= endDate;
    }

    return false;
  };

  // Détection Chrome pour l'effet liquid glass
  useEffect(() => {
    const detectChrome = () => {
      const isChromeBrowser = /Chrome/.test(navigator.userAgent) && /Google Inc/.test(navigator.vendor);
      setIsChrome(isChromeBrowser);
    };
    detectChrome();
  }, []);

  // Déterminer si l'effet liquid glass doit être appliqué
  // Désactiver automatiquement si l'utilisateur préfère réduire les animations
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const shouldUseLiquidGlass = isChrome && liquidGlassEnabled && !prefersReducedMotion;

  // Mise à jour de la position de la bulle selon le bouton actif
  useEffect(() => {
    const updateBubblePosition = () => {
      if (!navRef.current) return;

      const activeButton = navRef.current.querySelector('.nav-btn.active');
      if (!activeButton) return;

      const navRect = navRef.current.getBoundingClientRect();
      const buttonRect = activeButton.getBoundingClientRect();

      const newBubbleStyle = {
        left: buttonRect.left - navRect.left,
        top: buttonRect.top - navRect.top,
        width: buttonRect.width,
        height: buttonRect.height
      };

      // Calculer la distance de mouvement pour l'effet de déterminement léger
      const distance = Math.sqrt(
        Math.pow(newBubbleStyle.left - bubbleStyle.left, 2) +
        Math.pow(newBubbleStyle.top - bubbleStyle.top, 2)
      );

      // Ajouter l'effet de transition si la distance est significative
      if (distance > 5) {
        const bubbleElement = navRef.current.querySelector('.nav-bubble');
        if (bubbleElement) {
          // Détecter la direction du mouvement (gauche/droite)
          const horizontalMovement = newBubbleStyle.left - bubbleStyle.left;
          let direction = null;

          if (Math.abs(horizontalMovement) > 10) {
            direction = horizontalMovement > 0 ? 'right' : 'left';
            bubbleElement.setAttribute('data-direction', direction);
          }

          bubbleElement.setAttribute('data-transitioning', 'true');

          // Retirer les attributs après l'animation
          setTimeout(() => {
            if (bubbleElement) {
              bubbleElement.removeAttribute('data-transitioning');
              bubbleElement.removeAttribute('data-direction');
            }
          }, 900); // Durée totale de l'animation (étirement + transition)
        }
      }

      setBubbleStyle(newBubbleStyle);
    };

    // Calculer initialement
    updateBubblePosition();

    // Recalculer après un court délai pour s'assurer que le DOM est prêt
    const timer = setTimeout(updateBubblePosition, 100);

    return () => clearTimeout(timer);
  }, [location.pathname, isMenuOpen, bubbleStyle.left, bubbleStyle.top]);

  // Gestion du drag pour les toggle switches avec effet liquid glass
  const handleToggleMouseDown = (toggleId) => {
    if (!shouldUseLiquidGlass) return;
    setActiveToggle(toggleId);
  };

  const handleToggleMouseUp = () => {
    if (!shouldUseLiquidGlass) return;
    setTimeout(() => setActiveToggle(null), 50);
  };

  useEffect(() => {
    if (!shouldUseLiquidGlass) return;

    const handleGlobalMouseUp = () => {
      setActiveToggle(null);
    };

    window.addEventListener('mouseup', handleGlobalMouseUp);
    window.addEventListener('touchend', handleGlobalMouseUp);

    return () => {
      window.removeEventListener('mouseup', handleGlobalMouseUp);
      window.removeEventListener('touchend', handleGlobalMouseUp);
    };
  }, [shouldUseLiquidGlass]);

  // Enregistrer dynamiquement les bundles de traductions Accessibilité
  useEffect(() => {
    try {
      i18n.addResourceBundle('fr', 'translation', accessibilityFr, true, true);
      i18n.addResourceBundle('en', 'translation', accessibilityEn, true, true);
      i18n.addResourceBundle('de', 'translation', accessibilityDe, true, true);
      i18n.addResourceBundle('es', 'translation', accessibilityEs, true, true);
      i18n.addResourceBundle('it', 'translation', accessibilityIt, true, true);
    } catch {
      // noop
    }
  }, [i18n]);

  const allParksData = {
    europe: [
      // Bellewaerde
      { id: 'bellewaerde', name: t('parks.bellewaerde'), path: '/bellewaerde', logo: '/images/logos/logo-bellewaerde.png' },

      // Bobbejaanland
      { id: 'bobbejaanland', name: t('parks.bobbejaanland'), path: '/bobbejaanland', logo: '/images/logos/logo-bobbejaanland.png' },

      // Disneyland Paris Resort
      { id: 'dlp', name: t('parks.dlp'), path: '/', logo: '/images/logos/logo-dlp.png' },
      { id: 'wds', name: t('parks.wds'), path: '/disney-adventure-world', logo: '/images/logos/logo-daw.png' },

      // Efteling
      { id: 'eft', name: t('parks.eft'), path: '/efteling', logo: '/images/logos/logo-efteling.png' },

      // Energylandia
      { id: 'energylandia', name: t('parks.energylandia'), path: '/energylandia', logo: '/images/logos/logo-energylandia.png' },

      // Europa Park
      { id: 'ep', name: t('parks.europaPark'), path: '/europa-park', logo: '/images/logos/logo-ep.png' },

      // Futuroscope
      { id: 'futuroscope', name: t('parks.futuroscope'), path: '/futuroscope', logo: '/images/logos/logo-futuro.png' },

      // Puy du Fou
      { id: 'puydufou', name: t('parks.puyDuFou', 'Le Puy du Fou'), path: '/puydufou', logo: '/images/logos/logo-puydufou.png' },

      // Gardaland
      { id: 'gardaland', name: t('parks.gardaland'), path: '/gardaland', logo: '/images/logos/logo-gardaland.png' },

      // Gröna Lund
      { id: 'gronaLund', name: t('parks.gronaLund'), path: '/grona-lund', logo: '/images/logos/logo-gronalund.png' },

      // Le Pal
      { id: 'lePal', name: t('parks.lePal'), path: '/le-pal', logo: '/images/logos/logo-lepal.png' },

      // Mirabilandia
      { id: 'mirabilandia', name: t('parks.mirabilandia'), path: '/mirabilandia', logo: '/images/logos/logo-mirabilandia.png' },

      // Movie Park Germany
      { id: 'movieParkGermany', name: t('parks.movieParkGermany'), path: '/movie-park-germany', logo: '/images/logos/logo-movie-park-germany.png' },

      // Nigloland
      { id: 'niglo', name: t('parks.nigloland'), path: '/nigloland', logo: '/images/logos/logo-niglo.png' },

      // Parc Astérix
      { id: 'ast', name: t('parks.parcAsterix'), path: '/parc-asterix', logo: '/images/logos/logo-asterix.png' },

      // Parque de Atracciones de Madrid
      { id: 'parqueDeAtraccionesDeMadrid', name: t('parks.parqueDeAtraccionesDeMadrid'), path: '/parque-de-atracciones-de-madrid', logo: '/images/logos/logo-parque-de-atracciones-de-madrid.png' },

      // Phantasialand
      { id: 'pha', name: t('parks.phantasialand'), path: '/phantasialand', logo: '/images/logos/logo-phantasialand.png' },

      // Plopsaland Belgium
      { id: 'plopsalandDepanne', name: t('parks.plopsalandDepanne'), path: '/plopsaland-belgium', logo: '/images/logos/logo-plopsapanne.png' },

      // PortAventura Park
      { id: 'portaventura', name: t('parks.portaventura'), path: '/portaventura', logo: '/images/logos/logo-portaventura.png' },

      // Thorpe Park
      { id: 'thorpePark', name: t('parks.thorpePark'), path: '/thorpe-park', logo: '/images/logos/logo-thorpepark.png' },

      // Toverland
      { id: 'tov', name: t('parks.toverland'), path: '/toverland', logo: '/images/logos/logo-toverland.png' },

      // Walibi (groupés par ordre alphabétique)
      { id: 'walibiBelgium', name: t('parks.walibiBelgium'), path: '/walibi-belgium', logo: '/images/logos/logo-walibibelgium.png' },
      { id: 'walibiHolland', name: t('parks.walibiHolland'), path: '/walibi-holland', logo: '/images/logos/logo-walibi-holland.png' },
      { id: 'walibiRhoneAlpes', name: t('parks.walibiRhoneAlpes'), path: '/walibi-rhone-alpes', logo: '/images/logos/logo-walibi-rhone-alpes.png' },

      // Alton Towers
      { id: 'altonTowers', name: 'Alton Towers', path: '/alton-towers', logo: '/images/logos/logo-alton-towers.png' },

      // Heide Park
      { id: 'heidePark', name: 'Heide Park', path: '/heide-park', logo: '/images/logos/logo-heide-park.png' },

      // LEGOLAND Deutschland
      { id: 'legolandDeutschland', name: 'LEGOLAND Deutschland', path: '/legoland-deutschland', logo: '/images/logos/logo-legoland-deutschland.png' },

    ],
    northAmerica: [
      // Busch Gardens Tampa Bay
      { id: 'buschgardens', name: t('parks.buschGardens'), path: '/busch-gardens-tampa', logo: '/images/logos/logo-bushgardentampa.png' },

      // Disneyland Resort (Californie)
      { id: 'disneylandCalifornia', name: t('parks.disneylandCalifornia'), path: '/disneyland-california', logo: '/images/logos/logo-disneylandparkca.png' },
      { id: 'disneyCaliforniaAdventure', name: t('parks.disneyCaliforniaAdventure'), path: '/disney-california-adventure', logo: '/images/logos/logo-disneylandcaliforniadventure.png' },

      // Universal Studios California
      { id: 'universalStudiosCalifornia', name: t('parks.universalStudiosCalifornia'), path: '/universal-studios-california', logo: '/images/logos/logo-universal-studios-hollywood.png' },

      // Knott's Berry Farm
      { id: 'knotts', name: t('parks.knottsBerryFarm'), path: '/knotts-berry-farm', logo: '/images/logos/logo-knotts.png' },

      // LEGOLAND Florida
      { id: 'legoland', name: t('parks.legolandFlorida'), path: '/legoland-florida', logo: '/images/logos/logo-legolandflorida.png' },

      // SeaWorld Orlando
      { id: 'seaworld', name: t('parks.seaWorld'), path: '/seaworld-orlando', logo: '/images/logos/logo-seaworld.png' },

      // Universal Orlando Resort
      { id: 'usf', name: t('parks.usf'), path: '/universal-studios', logo: '/images/logos/logo-unistudio.png' },
      { id: 'ioa', name: t('parks.ioa'), path: '/islands-of-adventure', logo: '/images/logos/logo-uniislands.png' },
      { id: 'epu', name: t('parks.epu'), path: '/epic-universe', logo: '/images/logos/logo-epicuniverse.png' },

      // Walt Disney World Resort
      { id: 'magicKingdom', name: t('parks.magicKingdom'), path: '/magic-kingdom', logo: '/images/logos/logo-wdw-magickingdom.png' },
      { id: 'epcot', name: t('parks.epcot'), path: '/epcot', logo: '/images/logos/logo-wdw-epcot.png' },
      { id: 'hollywoodStudios', name: t('parks.hollywoodStudios'), path: '/hollywood-studios', logo: '/images/logos/logo-wdw-hollywoodstudios.png' },
      { id: 'animalKingdom', name: t('parks.animalKingdom'), path: '/animal-kingdom', logo: '/images/logos/logo-wdw-animal-kingdom.png' },
      { id: 'typhoonLagoon', name: t('parks.typhoonLagoon'), path: '/typhoon-lagoon', logo: '/images/logos/logo-wdw-typhoon-lagoon.png' },
      { id: 'blizzardBeach', name: t('parks.blizzardBeach'), path: '/blizzard-beach', logo: '/images/logos/logo-wdw-blizzard-beach.png' },

      // Volcano Bay (Universal Orlando)
      { id: 'volcanoBay', name: "Universal's Volcano Bay", path: '/volcano-bay', logo: '/images/logos/logo-volcano-bay.png' },

      // Carowinds
      { id: 'carowinds', name: 'Carowinds', path: '/carowinds', logo: '/images/logos/logo-carowinds.png' },

      // Hersheypark
      { id: 'hersheypark', name: 'Hersheypark', path: '/hersheypark', logo: '/images/logos/logo-hershey-park.png' },

      // Kennywood
      { id: 'kennywood', name: 'Kennywood', path: '/kennywood', logo: '/images/logos/logo-kennywood.png' },

      // Kings Island
      { id: 'kingsIsland', name: 'Kings Island', path: '/kings-island', logo: '/images/logos/logo-kings-island.png' },

      // Knoebels
      { id: 'knoebels', name: 'Knoebels Amusement Park', path: '/knoebels', logo: '/images/logos/logo-knoebels.png' },

      // La Ronde (Canada)
      { id: 'laRonde', name: 'La Ronde', path: '/la-ronde', logo: '/images/logos/logo-la-ronde.png' },

      // Silver Dollar City
      { id: 'silverDollarCity', name: 'Silver Dollar City', path: '/silver-dollar-city', logo: '/images/logos/logo-silver-dollar-city.png' }
    ],
    asia: [
      // Hong Kong Disneyland Park
      { id: 'hongKongDisneylandPark', name: t('parks.hongKongDisneylandPark'), path: '/hong-kong-disneyland-park', logo: '/images/logos/logo-hong-kong-disneyland-park.png' },

      // Lotte World
      { id: 'lotteWorld', name: t('parks.lotteWorld'), path: '/lotte-world', logo: '/images/logos/logo-lotte-world.png' },

      // Shanghai Disneyland
      { id: 'shanghaiDisneyland', name: t('parks.shanghaiDisneyland'), path: '/shanghai-disneyland', logo: '/images/logos/logo-shanghai-disneyland.png' },

      // Six Flags Qiddiya City
      { id: 'sixFlagsQiddiyacity', name: t('parks.sixFlagsQiddiyacity'), path: '/six-flags-qiddiyacity', logo: '/images/logos/logo-sfq.png' },

      // Tokyo Disneyland
      { id: 'tokyoDisneyland', name: t('parks.tokyoDisneyland'), path: '/tokyo-disneyland', logo: '/images/logos/logo-tokyo-disneyland.png' },

      // Tokyo DisneySea
      { id: 'tokyoDisneysea', name: 'Tokyo DisneySea', path: '/tokyo-disneysea', logo: '/images/logos/logo-tokyo-disney-sea.png' },

      // Universal Studios Beijing
      { id: 'universalStudiosBeijing', name: t('parks.universalStudiosBeijing'), path: '/universal-studios-beijing', logo: '/images/logos/logo-universal-studios-beijing.png' },

      // Universal Studios Singapore
      { id: 'universalStudiosSingapore', name: 'Universal Studios Singapore', path: '/universal-studios-singapore', logo: '/images/logos/logo-universal-studios-singapore.png' }
    ]
  };

  // Filtrer les parcs en fonction des favoris et du mode réorganisation
  const getFilteredParks = (continent) => {
    if (continent === 'favorites') {
      const allParks = [
        ...allParksData.europe,
        ...allParksData.northAmerica,
        ...allParksData.asia
      ];

      const favoriteParks = allParks.filter((park) => visibleFavoriteParkIdsSet.has(park.id));

      // En dehors du mode réorganisation, appliquer l'ordre personnalisé spécifique aux favoris
      if (!isReorderMode) {
        return applyCustomOrder(favoriteParks, 'favorites');
      }

      // En mode réorganisation, utiliser la liste temporaire si définie
      return tempParks.length > 0 ? tempParks : favoriteParks;
    }

    if (!continent || !allParksData[continent]) return [];

    const parks = allParksData[continent];

    // Appliquer l'ordre personnalisé si pas en mode réorganisation
    if (!isReorderMode) {
      return applyCustomOrder(parks, continent);
    }

    // En mode réorganisation, utiliser tempParks si disponible
    return tempParks.length > 0 ? tempParks : parks;
  };

  // Logique de scroll pour masquer/afficher la barre de navigation (optimisée)
  const lastScrollYRef = useRef(0);
  const tickingRef = useRef(false);

  useEffect(() => {
    const handleScroll = () => {
      if (!tickingRef.current) {
        requestAnimationFrame(() => {
          const currentScrollY = window.scrollY;

          // Si on scroll vers le bas, masquer la barre
          if (currentScrollY > lastScrollYRef.current && currentScrollY > 100) {
            if (isVisibleRef.current !== false) {
              isVisibleRef.current = false;
              setIsVisible(false);
            }
          }
          // Si on scroll vers le haut, afficher la barre
          else if (currentScrollY < lastScrollYRef.current) {
            if (isVisibleRef.current !== true) {
              isVisibleRef.current = true;
              setIsVisible(true);
            }
          }

          lastScrollYRef.current = currentScrollY;
          tickingRef.current = false;
        });
        tickingRef.current = true;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []); // Plus de dépendance à lastScrollY - attache le listener une seule fois

  // Toujours activer les graphiques
  useEffect(() => {
    setGraphEnabled(true);
    localStorage.setItem('graphEnabled', JSON.stringify(true));
  }, [setGraphEnabled]);

  // Indicateur pluie: calcule s'il va pleuvoir dans l'heure pour afficher un point rouge sur le bouton météo
  useEffect(() => {
    let intervalId;

    const supportsWeather = isParkWeatherSupported(location.pathname);
    if (!supportsWeather) {
      setHasRainAlert(false);
      return;
    }

    const fetchRainAlert = async () => {
      try {
        const coords = getParkCoordinates(location.pathname);
        const latitude = coords?.latitude || coords?.lat;
        const longitude = coords?.longitude || coords?.lng;
        if (!latitude || !longitude) {
          setHasRainAlert(false);
          return;
        }

        const API_BASE_URL = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production'
          ? 'https://api-internal.magicparktimes.com'
          : 'http://localhost:3001');

        const cacheKey = `bottom-nav-weather::${location.pathname}::${latitude},${longitude}`;
        const now = Date.now();
        const cached = BOTTOM_NAV_WEATHER_CACHE.get(cacheKey);
        if (cached && now < cached.expiry) {
          setHasRainAlert(cached.value);
          return;
        }

        const params = new URLSearchParams({
          lat: String(latitude),
          lon: String(longitude)
        });

        if (!BOTTOM_NAV_WEATHER_INFLIGHT.has(cacheKey)) {
          const request = (async () => {
            const response = await fetch(`${API_BASE_URL}/api/weather-enhanced?${params.toString()}`);
            if (!response.ok) {
              return false;
            }

            const data = await response.json();
            const summary = getRainSummaryFromHourly(data?.hourly);
            const willRain = !!summary?.willRain;
            BOTTOM_NAV_WEATHER_CACHE.set(cacheKey, {
              value: willRain,
              expiry: Date.now() + BOTTOM_NAV_WEATHER_TTL
            });
            return willRain;
          })().finally(() => {
            BOTTOM_NAV_WEATHER_INFLIGHT.delete(cacheKey);
          });

          BOTTOM_NAV_WEATHER_INFLIGHT.set(cacheKey, request);
        }

        const willRain = await BOTTOM_NAV_WEATHER_INFLIGHT.get(cacheKey);
        setHasRainAlert(!!willRain);
      } catch {
        setHasRainAlert(false);
      }
    };

    // Appel immédiat puis rafraîchissement toutes les 5 minutes
    fetchRainAlert();
    intervalId = window.setInterval(fetchRainAlert, 5 * 60 * 1000);

    return () => {
      if (intervalId) window.clearInterval(intervalId);
    };
  }, [location.pathname]);

  const toggleMenu = () => {
    triggerNavHaptic();
    if (isMenuOpen) {
      // Fermeture avec animation
      setIsMenuClosing(true);
      setTimeout(() => {
        setIsMenuOpen(false);
        setIsMenuClosing(false);
      }, 300); // Durée de l'animation
    } else {
      // Ouverture
      setIsMenuOpen(true);
      // Fermer le modal météo si ouvert
      if (showWeatherModal) {
        closeWeatherModal();
      }
    }
  };

  const closeWeatherModal = () => {
    setIsWeatherClosing(true);
    setTimeout(() => {
      setShowWeatherModal(false);
      setIsWeatherClosing(false);
    }, 300); // Durée de l'animation
  };

  const toggleWeatherModal = () => {
    triggerNavHaptic();
    if (showWeatherModal) {
      // Fermeture avec animation
      closeWeatherModal();
    } else {
      // Ouverture
      setShowWeatherModal(true);
      // Fermer le menu si ouvert
      if (isMenuOpen) {
        closeMenu();
      }
    }
  };

  const closeMenu = useCallback(() => {
    setIsMenuClosing(true);
    setTimeout(() => {
      setIsMenuOpen(false);
      setIsMenuClosing(false);
    }, 300); // Durée de l'animation

    if (typeof setIsLanguageMenuOpen === 'function') {
      setIsLanguageMenuOpen(false);
    }
  }, [setIsLanguageMenuOpen]);

  // Fermer le menu au clic en dehors
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Vérifier si l'élément cliqué est dans le menu ou sur le bouton
      const menuElement = document.querySelector('.bottom-menu-content');
      const menuButton = document.querySelector('.menu-toggle-btn');

      // Si le menu est ouvert et le clic est en dehors
      if (isMenuOpen &&
        menuElement &&
        !menuElement.contains(event.target) &&
        menuButton &&
        !menuButton.contains(event.target)) {
        closeMenu();
      }
    };

    if (isMenuOpen) {
      // Utiliser setTimeout pour s'assurer que le DOM est prêt
      setTimeout(() => {
        document.addEventListener('mousedown', handleClickOutside);
      }, 100);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMenuOpen, closeMenu]);

  // Bloquer le scroll quand le menu est ouvert et détection de la barre d'adresse
  useEffect(() => {
    if (isMenuOpen) {
      // Bloquer le scroll de la page
      document.body.style.overflow = 'hidden';

      // Détection de la barre d'adresse mobile (même logique que WeatherModal)
      const detectMobileBrowserBar = () => {
        const viewportHeight = window.innerHeight;
        const visualViewportHeight = window.visualViewport?.height || viewportHeight;

        if (window.visualViewport && viewportHeight > visualViewportHeight + 100) {
          document.body.classList.add('mobile-browser-bar-present');
        } else {
          document.body.classList.remove('mobile-browser-bar-present');
        }
      };

      // Ajouter des gestionnaires d'événements clavier pour l'accessibilité
      const handleKeyDown = (event) => {
        if (event.key === 'Escape') {
          closeMenu();
        }
      };

      document.addEventListener('keydown', handleKeyDown);

      // Détection initiale
      detectMobileBrowserBar();

      // Écouter les changements de la visual viewport
      if (window.visualViewport) {
        window.visualViewport.addEventListener('resize', detectMobileBrowserBar);
      }

      // Nettoyage
      return () => {
        document.body.style.overflow = 'unset';
        document.body.classList.remove('mobile-browser-bar-present');
        if (window.visualViewport) {
          window.visualViewport.removeEventListener('resize', detectMobileBrowserBar);
        }
        document.removeEventListener('keydown', handleKeyDown);
      };
    } else {
      // Restaurer le scroll et nettoyer les classes
      document.body.style.overflow = 'unset';
      document.body.classList.remove('mobile-browser-bar-present');
    }
  }, [isMenuOpen, closeMenu]);

  const handleParkSelect = (park) => {
    triggerNavHaptic();
    closeMenu();
    navigate(park.path);
  };

  const handleContinentSelect = (continent) => {
    setSelectedContinent(continent);
    // Réinitialiser le mode réorganisation si on change de continent
    if (isReorderMode) {
      setIsReorderMode(false);
      setTempParks([]);
    }
  };

  const handleEnterReorderMode = (continent = selectedContinent) => {
    const currentParks = getFilteredParks(continent);
    setSelectedContinent(continent);
    setTempParks([...currentParks]);
    setIsReorderMode(true);
  };

  const handleSaveOrder = () => {
    const parkIds = tempParks.map(park => park.id);
    saveCustomParkOrder(selectedContinent, parkIds);
    setIsReorderMode(false);
    setTempParks([]);
  };

  const handleCancelReorder = () => {
    setIsReorderMode(false);
    setTempParks([]);
  };

  const handleResetOrder = () => {
    resetParkOrder(selectedContinent);
    setIsReorderMode(false);
    setTempParks([]);
  };

  const handleMoveParkUp = (index) => {
    const newParks = moveParkUp(tempParks, index);
    setTempParks(newParks);
  };

  const handleMoveParkDown = (index) => {
    const newParks = moveParkDown(tempParks, index);
    setTempParks(newParks);
  };

  const toggleHideNavLabels = () => {
    setHideNavLabels((prev) => {
      const next = !prev;
      localStorage.setItem('hideNavLabels', String(next));
      return next;
    });
  };

  // Fonction utilitaire pour créer les éléments d'image avec support WebP
  const createButtonImage = (imageName, alt, isDarkMode) => {
    const suffix = isDarkMode ? '_WHITE' : '_BLACK';
    const webpPath = `/images/buttons/webp/${imageName}${suffix}.webp`;
    const pngPath = `/images/buttons/${imageName}${suffix}.png`;

    return (
      <picture>
        <source srcSet={webpPath} type="image/webp" />
        <source srcSet={pngPath} type="image/png" />
        <img
          src={pngPath}
          alt=""
          aria-hidden="true"
          className="btn-image"
          loading="lazy"
        />
      </picture>
    );
  };

  // Déterminer les boutons selon le parc actuel
  const getNavigationButtons = () => {
    const currentPath = location.pathname;
    const supportsWeather = isParkWeatherSupported(currentPath);

    if (currentPath === '/' || (currentPath.includes('disneyland') && !currentPath.includes('disneyland-california') && !currentPath.includes('shanghai-disneyland') && !currentPath.includes('tokyo-disneyland') && !currentPath.includes('hong-kong-disneyland'))) {
      // Disneyland Paris - boutons spécifiques (pas de météo)
      return [
        { id: 'dlp', name: t('navigation.disneylandPark'), path: '/', icon: createButtonImage('Parc', 'Parc', isDarkMode), type: 'park' },
        { id: 'wds', name: t('navigation.waltDisneyStudios'), path: '/disney-adventure-world', icon: createButtonImage('Parc', 'Parc', isDarkMode), type: 'park' },
        { id: 'shows', name: t('filters.shows'), path: '/shows', icon: createButtonImage('SHOWS', t('filters.shows'), isDarkMode), type: 'feature' },
        { id: 'restaurants', name: t('filters.restaurants'), path: '/restaurants', icon: createButtonImage('Restaurant', t('filters.restaurants'), isDarkMode), type: 'feature' },
        { id: 'weather', name: t('navigation.weather'), path: '#', icon: createButtonImage('Meteo', t('navigation.weather'), isDarkMode), type: 'weather' },
        { id: 'menu', name: t('navigation.menu'), path: '#', icon: '☰', type: 'menu' }
      ];
    } else if (currentPath === '/disney-adventure-world' || currentPath === '/studios' || currentPath === '/walt-disney-studios') {
      // Disney Adventure World - boutons spécifiques (pas de météo)
      return [
        { id: 'dlp', name: t('navigation.disneylandPark'), path: '/', icon: createButtonImage('Parc', 'Parc', isDarkMode), type: 'park' },
        { id: 'wds', name: t('navigation.waltDisneyStudios'), path: '/disney-adventure-world', icon: createButtonImage('Parc', 'Parc', isDarkMode), type: 'park' },
        { id: 'shows', name: t('filters.shows'), path: '/shows', icon: createButtonImage('SHOWS', t('filters.shows'), isDarkMode), type: 'feature' },
        { id: 'restaurants', name: t('filters.restaurants'), path: '/restaurants', icon: createButtonImage('Restaurant', t('filters.restaurants'), isDarkMode), type: 'feature' },
        { id: 'weather', name: t('navigation.weather'), path: '#', icon: createButtonImage('Meteo', t('navigation.weather'), isDarkMode), type: 'weather' },
        { id: 'menu', name: t('navigation.menu'), path: '#', icon: '☰', type: 'menu' }
      ];
    } else if (currentPath === '/shows' || currentPath === '/restaurants' || currentPath.includes('my-pass')) {
      // Pages Spectacles, Restaurants DLP ou Pass - avec tous les boutons DLP car liées à Disneyland Paris (pas de météo)
      return [
        { id: 'dlp', name: t('navigation.disneylandPark'), path: '/', icon: createButtonImage('Parc', 'Parc', isDarkMode), type: 'park' },
        { id: 'wds', name: t('navigation.waltDisneyStudios'), path: '/disney-adventure-world', icon: createButtonImage('Parc', 'Parc', isDarkMode), type: 'park' },
        { id: 'shows', name: t('filters.shows'), path: '/shows', icon: createButtonImage('SHOWS', t('filters.shows'), isDarkMode), type: 'feature' },
        { id: 'restaurants', name: t('filters.restaurants'), path: '/restaurants', icon: createButtonImage('Restaurant', t('filters.restaurants'), isDarkMode), type: 'feature' },
        { id: 'weather', name: t('navigation.weather'), path: '#', icon: createButtonImage('Meteo', t('navigation.weather'), isDarkMode), type: 'weather' },
        { id: 'menu', name: t('navigation.menu'), path: '#', icon: '☰', type: 'menu' }
      ];
    } else if (currentPath === '/dispatch-max') {
      return [
        {
          id: 'dashboard',
          name: t('auth.dashboard.title', 'Espace membre'),
          path: '/dashboard',
          activePaths: ['/dashboard', '/dispatch-max'],
          icon: <UserCheck size={18} aria-hidden="true" />,
          type: 'feature'
        },
        { id: 'menu', name: t('navigation.menu'), path: '#', icon: '☰', type: 'menu' }
      ];
    } else if (genericThemeparksContentPark) {
      const hasShows = genericThemeparksContentAvailability.hasShows;
      const hasRestaurants = genericThemeparksContentAvailability.hasRestaurants;
      const buttons = [
        {
          id: `${genericThemeparksContentPark.parkSlug}-park`,
          name: genericThemeparksContentPark.parkName,
          path: genericThemeparksContentPark.path,
          icon: createButtonImage('Parc', genericThemeparksContentPark.parkName, isDarkMode),
          type: 'park'
        },
        ...(hasShows ? [{
          id: `${genericThemeparksContentPark.parkSlug}-shows`,
          name: t('filters.shows'),
          path: `${genericThemeparksContentPark.path}/shows`,
          icon: createButtonImage('SHOWS', t('filters.shows'), isDarkMode),
          type: 'feature'
        }] : []),
        ...(hasRestaurants ? [{
          id: `${genericThemeparksContentPark.parkSlug}-restaurants`,
          name: t('filters.restaurants'),
          path: `${genericThemeparksContentPark.path}/restaurants`,
          icon: createButtonImage('Restaurant', t('filters.restaurants'), isDarkMode),
          type: 'feature'
        }] : [])
      ];

      if (supportsWeather) {
        buttons.push({ id: 'weather', name: t('navigation.weather'), path: '#', icon: createButtonImage('Meteo', t('navigation.weather'), isDarkMode), type: 'weather' });
      }

      buttons.push({ id: 'menu', name: t('navigation.menu'), path: '#', icon: '☰', type: 'menu' });
      return buttons;
    } else if ((currentPath.includes('universal-studios') && !currentPath.includes('universal-studios-beijing') && !currentPath.includes('universal-studios-california')) || currentPath.includes('islands-of-adventure') || currentPath.includes('epic-universe') || currentPath.includes('volcano-bay')) {
      // Universal Orlando Resort
      const isUsf = currentPath.includes('universal-studios') && !currentPath.includes('universal-studios-beijing') && !currentPath.includes('universal-studios-california');
      const isIoa = currentPath.includes('islands-of-adventure');
      const isEpu = currentPath.includes('epic-universe');
      const showsPath = isUsf ? '/universal-studios/shows' : (isIoa ? '/islands-of-adventure/shows' : (isEpu ? '/epic-universe/shows' : null));

      const buttons = [
        { id: 'usf', name: t('navigation.usf'), path: '/universal-studios', icon: createButtonImage('Parc', 'Parc', isDarkMode), type: 'park' },
        { id: 'ioa', name: t('navigation.ioa'), path: '/islands-of-adventure', icon: createButtonImage('Parc', 'Parc', isDarkMode), type: 'park' },
        { id: 'epu', name: t('navigation.epu'), path: '/epic-universe', icon: createButtonImage('Parc', 'Parc', isDarkMode), type: 'park' },
        { id: 'volcanoBay', name: "Volcano Bay", path: '/volcano-bay', icon: createButtonImage('Parc', 'Parc', isDarkMode), type: 'park' },
      ];

      if (showsPath) {
        buttons.push({ id: 'shows', name: t('filters.shows'), path: showsPath, icon: createButtonImage('SHOWS', t('filters.shows'), isDarkMode), type: 'feature' });
      }

      if (supportsWeather) {
        buttons.push({ id: 'weather', name: t('navigation.weather'), path: '#', icon: createButtonImage('Meteo', t('navigation.weather'), isDarkMode), type: 'weather' });
      }

      buttons.push({ id: 'menu', name: t('navigation.menu'), path: '#', icon: '☰', type: 'menu' });
      return buttons;
    } else if (currentPath.includes('universal-studios-california')) {
      // Universal Studios California - boutons spécifiques
      const buttons = [
        { id: 'universalStudiosCalifornia', name: t('parks.universalStudiosCalifornia'), path: '/universal-studios-california', icon: createButtonImage('Parc', t('parks.universalStudiosCalifornia'), isDarkMode), type: 'park' },
        { id: 'shows', name: t('filters.shows'), path: '/universal-studios-california/shows', icon: createButtonImage('SHOWS', t('filters.shows'), isDarkMode), type: 'feature' }
      ];

      if (supportsWeather) {
        buttons.push({ id: 'weather', name: t('navigation.weather'), path: '#', icon: createButtonImage('Meteo', t('navigation.weather'), isDarkMode), type: 'weather' });
      }

      buttons.push({ id: 'menu', name: t('navigation.menu'), path: '#', icon: '☰', type: 'menu' });
      return buttons;
    } else if (currentPath.includes('universal-studios-beijing')) {
      // Universal Studios Beijing - boutons spécifiques
      const buttons = [
        { id: 'universalStudiosBeijing', name: t('parks.universalStudiosBeijing'), path: '/universal-studios-beijing', icon: createButtonImage('Parc', t('parks.universalStudiosBeijing'), isDarkMode), type: 'park' },
        { id: 'shows', name: t('filters.shows'), path: '/universal-studios-beijing/shows', icon: createButtonImage('SHOWS', t('filters.shows'), isDarkMode), type: 'feature' }
      ];

      if (supportsWeather) {
        buttons.push({ id: 'weather', name: t('navigation.weather'), path: '#', icon: createButtonImage('Meteo', t('navigation.weather'), isDarkMode), type: 'weather' });
      }

      buttons.push({ id: 'menu', name: t('navigation.menu'), path: '#', icon: '☰', type: 'menu' });
      return buttons;
    } else if (currentPath.includes('efteling')) {
      // Efteling - boutons spécifiques
      const buttons = [
        { id: 'eft', name: t('parks.eft'), path: '/efteling', icon: createButtonImage('Parc', t('parks.eft'), isDarkMode), type: 'park' },
        { id: 'shows', name: t('filters.shows'), path: '/efteling/shows', icon: createButtonImage('SHOWS', t('filters.shows'), isDarkMode), type: 'feature' }
      ];

      if (supportsWeather) {
        buttons.push({ id: 'weather', name: t('navigation.weather'), path: '#', icon: createButtonImage('Meteo', t('navigation.weather'), isDarkMode), type: 'weather' });
      }

      buttons.push({ id: 'menu', name: t('navigation.menu'), path: '#', icon: '☰', type: 'menu' });
      return buttons;
    } else if (currentPath.includes('phantasialand')) {
      // Phantasialand - boutons spécifiques
      const buttons = [
        { id: 'pha', name: t('parks.phantasialand'), path: '/phantasialand', icon: createButtonImage('Parc', t('parks.phantasialand'), isDarkMode), type: 'park' },
        { id: 'shows', name: t('filters.shows'), path: '/phantasialand/shows', icon: createButtonImage('SHOWS', t('filters.shows'), isDarkMode), type: 'feature' }
      ];

      if (supportsWeather) {
        buttons.push({ id: 'weather', name: t('navigation.weather'), path: '#', icon: createButtonImage('Meteo', t('navigation.weather'), isDarkMode), type: 'weather' });
      }

      buttons.push({ id: 'menu', name: t('navigation.menu'), path: '#', icon: '☰', type: 'menu' });
      return buttons;
    } else if (currentPath.includes('europa-park')) {
      const buttons = [
        { id: 'europapark', name: t('parks.europaPark'), path: '/europa-park', icon: createButtonImage('Parc', t('parks.europaPark'), isDarkMode), type: 'park' },
        { id: 'shows', name: t('filters.shows'), path: '/europa-park/shows', icon: createButtonImage('SHOWS', t('filters.shows'), isDarkMode), type: 'feature' },
        { id: 'rulantica', name: t('parks.rulantica'), path: '/rulantica', icon: createButtonImage('Parc', t('parks.rulantica'), isDarkMode), type: 'park' }
      ];

      if (isTraumaticaActive()) {
        buttons.push({ id: 'traumatica', name: t('parks.traumatica'), path: '/traumatica', icon: createButtonImage('Parc', t('parks.traumatica'), isDarkMode), type: 'park' });
      }

      if (supportsWeather) {
        buttons.push({ id: 'weather', name: t('navigation.weather'), path: '#', icon: createButtonImage('Meteo', t('navigation.weather'), isDarkMode), type: 'weather' });
      }

      buttons.push({ id: 'menu', name: t('navigation.menu'), path: '#', icon: '☰', type: 'menu' });
      return buttons;
    } else if (currentPath.includes('rulantica')) {
      // Rulantica - boutons spécifiques avec Europa-Park et Traumatica (Halloween)
      const buttons = [
        { id: 'europapark', name: t('parks.europaPark'), path: '/europa-park', icon: createButtonImage('Parc', t('parks.europaPark'), isDarkMode), type: 'park' },
        { id: 'rulantica', name: t('parks.rulantica'), path: '/rulantica', icon: createButtonImage('Parc', t('parks.rulantica'), isDarkMode), type: 'park' }
      ];

      if (isTraumaticaActive()) {
        buttons.push({ id: 'traumatica', name: t('parks.traumatica'), path: '/traumatica', icon: createButtonImage('Parc', t('parks.traumatica'), isDarkMode), type: 'park' });
      }

      if (supportsWeather) {
        buttons.push({ id: 'weather', name: t('navigation.weather'), path: '#', icon: createButtonImage('Meteo', t('navigation.weather'), isDarkMode), type: 'weather' });
      }

      buttons.push({ id: 'menu', name: t('navigation.menu'), path: '#', icon: '☰', type: 'menu' });
      return buttons;
    } else if (currentPath.includes('traumatica')) {
      // Traumatica - afficher les 3 parcs pendant Halloween (la page n'est accessible que pendant la saison)
      const buttons = [
        { id: 'europapark', name: t('parks.europaPark'), path: '/europa-park', icon: createButtonImage('Parc', t('parks.europaPark'), isDarkMode), type: 'park' },
        { id: 'rulantica', name: t('parks.rulantica'), path: '/rulantica', icon: createButtonImage('Parc', t('parks.rulantica'), isDarkMode), type: 'park' },
        { id: 'traumatica', name: t('parks.traumatica'), path: '/traumatica', icon: createButtonImage('Parc', t('parks.traumatica'), isDarkMode), type: 'park' },
      ];

      if (supportsWeather) {
        buttons.push({ id: 'weather', name: t('navigation.weather'), path: '#', icon: createButtonImage('Meteo', t('navigation.weather'), isDarkMode), type: 'weather' });
      }

      buttons.push({ id: 'menu', name: t('navigation.menu'), path: '#', icon: '☰', type: 'menu' });
      return buttons;
    } else if (currentPath.includes('magic-kingdom')) {
      // Magic Kingdom et ses restaurants - boutons spécifiques avec restaurants
      const buttons = [
        { id: 'magicKingdom', name: t('parks.magicKingdom'), path: '/magic-kingdom', icon: createButtonImage('Parc', t('parks.magicKingdom'), isDarkMode), type: 'park' },
        { id: 'shows', name: t('filters.shows'), path: '/magic-kingdom/shows', icon: createButtonImage('SHOWS', t('filters.shows'), isDarkMode), type: 'feature' },
        { id: 'restaurants', name: t('navigation.restaurants'), path: '/magic-kingdom/restaurants', icon: createButtonImage('Restaurant', t('navigation.restaurants'), isDarkMode), type: 'feature' }
      ];

      if (supportsWeather) {
        buttons.push({ id: 'weather', name: t('navigation.weather'), path: '#', icon: createButtonImage('Meteo', t('navigation.weather'), isDarkMode), type: 'weather' });
      }

      buttons.push({ id: 'menu', name: t('navigation.menu'), path: '#', icon: '☰', type: 'menu' });
      return buttons;
    } else if (currentPath.includes('epcot')) {
      // EPCOT et ses restaurants - boutons spécifiques avec restaurants
      const buttons = [
        { id: 'epcot', name: t('parks.epcot'), path: '/epcot', icon: createButtonImage('Parc', t('parks.epcot'), isDarkMode), type: 'park' },
        { id: 'shows', name: t('filters.shows'), path: '/epcot/shows', icon: createButtonImage('SHOWS', t('filters.shows'), isDarkMode), type: 'feature' },
        { id: 'restaurants', name: t('navigation.restaurants'), path: '/epcot/restaurants', icon: createButtonImage('Restaurant', t('navigation.restaurants'), isDarkMode), type: 'feature' }
      ];

      if (supportsWeather) {
        buttons.push({ id: 'weather', name: t('navigation.weather'), path: '#', icon: createButtonImage('Meteo', t('navigation.weather'), isDarkMode), type: 'weather' });
      }

      buttons.push({ id: 'menu', name: t('navigation.menu'), path: '#', icon: '☰', type: 'menu' });
      return buttons;
    } else if (currentPath.includes('hollywood-studios')) {
      // Disney's Hollywood Studios et ses restaurants - boutons spécifiques avec restaurants
      const buttons = [
        { id: 'hollywoodStudios', name: t('parks.hollywoodStudios'), path: '/hollywood-studios', icon: createButtonImage('Parc', t('parks.hollywoodStudios'), isDarkMode), type: 'park' },
        { id: 'shows', name: t('filters.shows'), path: '/hollywood-studios/shows', icon: createButtonImage('SHOWS', t('filters.shows'), isDarkMode), type: 'feature' },
        { id: 'restaurants', name: t('navigation.restaurants'), path: '/hollywood-studios/restaurants', icon: createButtonImage('Restaurant', t('navigation.restaurants'), isDarkMode), type: 'feature' }
      ];

      if (supportsWeather) {
        buttons.push({ id: 'weather', name: t('navigation.weather'), path: '#', icon: createButtonImage('Meteo', t('navigation.weather'), isDarkMode), type: 'weather' });
      }

      buttons.push({ id: 'menu', name: t('navigation.menu'), path: '#', icon: '☰', type: 'menu' });
      return buttons;
    } else if (currentPath.includes('animal-kingdom')) {
      // Disney's Animal Kingdom et ses restaurants - boutons spécifiques avec restaurants
      const buttons = [
        { id: 'animalKingdom', name: t('parks.animalKingdom'), path: '/animal-kingdom', icon: createButtonImage('Parc', t('parks.animalKingdom'), isDarkMode), type: 'park' },
        { id: 'shows', name: t('filters.shows'), path: '/animal-kingdom/shows', icon: createButtonImage('SHOWS', t('filters.shows'), isDarkMode), type: 'feature' },
        { id: 'restaurants', name: t('navigation.restaurants'), path: '/animal-kingdom/restaurants', icon: createButtonImage('Restaurant', t('navigation.restaurants'), isDarkMode), type: 'feature' }
      ];

      if (supportsWeather) {
        buttons.push({ id: 'weather', name: t('navigation.weather'), path: '#', icon: createButtonImage('Meteo', t('navigation.weather'), isDarkMode), type: 'weather' });
      }

      buttons.push({ id: 'menu', name: t('navigation.menu'), path: '#', icon: '☰', type: 'menu' });
      return buttons;
    } else if (currentPath.includes('parc-asterix') || currentPath.includes('asterix-pass')) {
      // Parc Astérix et Pass Astérix - boutons spécifiques avec météo
      const buttons = [
        { id: 'ast', name: t('parks.parcAsterix'), path: '/parc-asterix', icon: createButtonImage('Parc', t('navigation.park'), isDarkMode), type: 'park' },
        { id: 'shows', name: t('filters.shows'), path: '/parc-asterix/shows', icon: createButtonImage('SHOWS', t('filters.shows'), isDarkMode), type: 'feature' },
        { id: 'restaurants', name: t('filters.restaurants'), path: '/parc-asterix/restaurants', icon: createButtonImage('Restaurant', t('filters.restaurants'), isDarkMode), type: 'feature' },
      ];

      if (supportsWeather) {
        buttons.push({ id: 'weather', name: t('navigation.weather'), path: '#', icon: createButtonImage('Meteo', t('navigation.weather'), isDarkMode), type: 'weather' });
      }

      buttons.push({ id: 'menu', name: t('navigation.menu'), path: '#', icon: '☰', type: 'menu' });
      return buttons;
    } else if (currentPath.includes('portaventura/ferrari-land')) {
      // Ferrari Land - boutons spécifiques
      const buttons = [
        { id: 'portaventura', name: t('parks.portaventura'), path: '/portaventura', icon: createButtonImage('Parc', t('parks.portaventura'), isDarkMode), type: 'park' },
        { id: 'ferrariland', name: 'Ferrari Land', path: '/portaventura/ferrari-land', icon: createButtonImage('Parc', 'Ferrari Land', isDarkMode), type: 'park' }
      ];

      if (supportsWeather) {
        buttons.push({ id: 'weather', name: t('navigation.weather'), path: '#', icon: createButtonImage('Meteo', t('navigation.weather'), isDarkMode), type: 'weather' });
      }

      buttons.push({ id: 'menu', name: t('navigation.menu'), path: '#', icon: '☰', type: 'menu' });
      return buttons;
    } else if (currentPath.includes('portaventura')) {
      // PortAventura - boutons spécifiques avec Ferrari Land
      const buttons = [
        { id: 'portaventura', name: t('parks.portaventura'), path: '/portaventura', icon: createButtonImage('Parc', t('parks.portaventura'), isDarkMode), type: 'park' },
        { id: 'ferrariland', name: 'Ferrari Land', path: '/portaventura/ferrari-land', icon: createButtonImage('Parc', 'Ferrari Land', isDarkMode), type: 'park' }
      ];

      if (supportsWeather) {
        buttons.push({ id: 'weather', name: t('navigation.weather'), path: '#', icon: createButtonImage('Meteo', t('navigation.weather'), isDarkMode), type: 'weather' });
      }

      buttons.push({ id: 'menu', name: t('navigation.menu'), path: '#', icon: '☰', type: 'menu' });
      return buttons;
    } else if (currentPath.includes('disneyland-california') || currentPath.includes('disney-california-adventure')) {
      // Disneyland Resort California - boutons spécifiques avec les deux parcs du resort
      const isDca = currentPath.includes('disney-california-adventure');
      const showsPath = isDca ? '/disney-california-adventure/shows' : '/disneyland-california/shows';

      const buttons = [
        { id: 'disneylandCalifornia', name: t('parks.disneylandCalifornia'), path: '/disneyland-california', icon: createButtonImage('Parc', t('parks.disneylandCalifornia'), isDarkMode), type: 'park' },
        { id: 'disneyCaliforniaAdventure', name: t('parks.disneyCaliforniaAdventure'), path: '/disney-california-adventure', icon: createButtonImage('Parc', t('parks.disneyCaliforniaAdventure'), isDarkMode), type: 'park' },
        { id: 'shows', name: t('filters.shows'), path: showsPath, icon: createButtonImage('SHOWS', t('filters.shows'), isDarkMode), type: 'feature' }
      ];

      if (supportsWeather) {
        buttons.push({ id: 'weather', name: t('navigation.weather'), path: '#', icon: createButtonImage('Meteo', t('navigation.weather'), isDarkMode), type: 'weather' });
      }

      buttons.push({ id: 'menu', name: t('navigation.menu'), path: '#', icon: '☰', type: 'menu' });
      return buttons;
    } else {
      // Autres parcs - boutons génériques avec météo si supportée
      const buttons = [
        { id: 'park', name: t('navigation.park'), path: location.pathname, icon: createButtonImage('Parc', t('navigation.park'), isDarkMode), type: 'park' }
      ];

      if (supportsWeather) {
        buttons.push({ id: 'weather', name: t('navigation.weather'), path: '#', icon: createButtonImage('Meteo', t('navigation.weather'), isDarkMode), type: 'weather' });
      }

      buttons.push({ id: 'menu', name: t('navigation.menu'), path: '#', icon: '☰', type: 'menu' });
      return buttons;
    }
  };

  const navigationButtons = getNavigationButtons();

  // Ne pas afficher sur desktop
  if (!isMobile()) {
    return null;
  }

  return (
    <>
      {/* Filtres SVG Liquid Glass optimisés si activé */}
      {shouldUseLiquidGlass && (
        <svg className="liquid-glass-filter" aria-hidden="true">
          <defs>
            {/* Filtre de déformation type "liquid glass" (inspiré de lg-dist) */}
            <filter id="mpt-lg-dist" x="0%" y="0%" width="100%" height="100%">
              <feTurbulence type="fractalNoise" baseFrequency="0.008 0.008" numOctaves="2" seed="92" result="noise" />
              <feGaussianBlur in="noise" stdDeviation="2" result="blurred" />
              <feDisplacementMap in="SourceGraphic" in2="blurred" scale="50" xChannelSelector="R" yChannelSelector="G" />
            </filter>

            {/* Filtre principal pour la navigation - statique pour éviter la déformation continue */}
            <filter id="liquid-glass-nav">
              <feTurbulence type="fractalNoise" baseFrequency="0.15" numOctaves="3" seed="8" result="noise" />
              <feGaussianBlur in="noise" stdDeviation="1.2" result="blurred-noise" />
              <feDisplacementMap
                xChannelSelector="R"
                yChannelSelector="G"
                scale="18"
                in="SourceGraphic"
                in2="blurred-noise"
                result="displaced"
              />
              <feBlend in="SourceGraphic" in2="displaced" mode="overlay" />
            </filter>

            {/* Filtre Goo optimisé pour effet liquide - uniquement lors des interactions */}
            <filter id="liquid-goo-filter">
              <feGaussianBlur in="SourceGraphic" stdDeviation="10" result="blur" />
              <feColorMatrix
                in="blur"
                mode="matrix"
                values="1 0 0 0 0
                        0 1 0 0 0
                        0 0 1 0 0
                        0 0 0 20 -10"
                result="goo"
              />
              <feBlend in="SourceGraphic" in2="goo" mode="normal" />
              <feComponentTransfer>
                <feFuncA type="gamma" amplitude="1.05" exponent="0.95" offset="0"/>
              </feComponentTransfer>
            </filter>

            {/* Filtre de reflection simplifié pour effet de verre */}
            <filter id="liquid-reflection-filter" x="-20%" y="-20%" width="140%" height="140%">
              <feGaussianBlur in="SourceAlpha" stdDeviation="2" result="blur" />
              <feOffset in="blur" dx="0" dy="1" result="offset-blur" />
              <feComposite in="offset-blur" in2="SourceAlpha" operator="arithmetic" k2="-1" k3="1" result="inner-shadow" />
              <feFlood floodColor="white" floodOpacity="0.5" result="color" />
              <feComposite in="color" in2="inner-shadow" operator="in" result="shadow" />
              <feComposite in="SourceGraphic" in2="shadow" operator="over" />
            </filter>

            {/* Filtre pour effet de transparence avancée - statique */}
            <filter id="liquid-transparency-filter" colorInterpolationFilters="sRGB">
              <feColorMatrix
                type="matrix"
                values="1 0 0 0 0
                        0 1 0 0 0
                        0 0 1 0 0
                        0 0 0 0.85 0.15"
                result="transparent"
              />
              <feGaussianBlur in="transparent" stdDeviation="1.5" />
            </filter>

            {/* Filtre de distortion minimal - sans animation continue */}
            <filter id="liquid-distortion-filter">
              <feTurbulence type="fractalNoise" baseFrequency="0.008" numOctaves="1" seed="5" result="turb" />
              <feDisplacementMap in="SourceGraphic" in2="turb" scale="8" xChannelSelector="R" yChannelSelector="B" />
            </filter>

            {/* Filtre glow simple pour effet de brillance */}
            <filter id="liquid-glow-filter" x="-50%" y="-50%" width="200%" height="200%">
              <feGaussianBlur stdDeviation="2" result="coloredBlur"/>
              <feMerge>
                <feMergeNode in="coloredBlur"/>
                <feMergeNode in="SourceGraphic"/>
              </feMerge>
            </filter>
          </defs>
        </svg>
      )}

      {/* Barre de navigation en bas */}
      <div
        ref={navRef}
        className={`bottom-navigation ${isVisible ? 'visible' : 'hidden'} ${isMenuOpen ? 'menu-open' : ''} ${hideNavLabels ? 'labels-hidden' : ''} ${shouldUseLiquidGlass ? 'liquid-glass-chrome' : ''}`}
      >
        {/* Bulle de sélection organique */}
        <div
          className={`nav-bubble ${bubbleStyle.width > 0 ? 'active' : ''}`}
          style={{
            left: `${bubbleStyle.left}px`,
            top: `${bubbleStyle.top}px`,
            width: `${bubbleStyle.width}px`,
            height: `${bubbleStyle.height}px`
          }}
        />

        {/* Boutons de navigation adaptés au parc */}
        {navigationButtons.map((button) => {
          const isButtonActive = Array.isArray(button.activePaths)
            ? button.activePaths.includes(location.pathname)
            : location.pathname === button.path;

          if (button.type === 'menu') {
            return (
              <button
                key={button.id}
                className={`nav-btn menu-btn ${isMenuOpen ? 'active' : ''}`}
                onClick={toggleMenu}
                aria-label={t('navigation.menu')}
              >
                <span className="btn-icon">{isMenuOpen ? '✕' : button.icon}</span>
                <span className="btn-text">{isMenuOpen ? t('navigation.close') : button.name}</span>
              </button>
            );
          } else if (button.type === 'weather') {
            return (
              <button
                key={button.id}
                className={`nav-btn weather-btn ${hasRainAlert ? 'has-rain-alert' : ''}`}
                onClick={toggleWeatherModal}
                title={button.name}
              >
                <span className="btn-icon">{button.icon}</span>
                <span className="btn-text">{button.name}</span>
              </button>
            );
          } else {
            return (
              <button
                key={button.id}
                onClick={() => {
                  triggerNavHaptic();
                  // Fermer tous les menus ouverts avant navigation
                  if (isMenuOpen) closeMenu();
                  if (showWeatherModal) closeWeatherModal();
                  navigate(button.path);
                }}
                className={`nav-btn ${isButtonActive ? 'active' : ''}`}
                title={button.name}
              >
                <span className="btn-icon">{button.icon}</span>
                <span className="btn-text">{button.name}</span>
              </button>
            );
          }
        })}
      </div>

      {/* Menu complet qui s'ouvre vers le haut */}
      {isMenuOpen && (
        /* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */
        <div
          className={`bottom-menu-overlay ${shouldUseLiquidGlass ? 'liquid-glass-chrome' : ''}`}
          onClick={(e) => {
            // Fermer si on clique sur l'overlay (pas sur le contenu)
            if (e.target === e.currentTarget) {
              closeMenu();
            }
          }}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              closeMenu();
            }
          }}
          role="dialog"
          aria-modal="true"
          aria-labelledby="menu-header"
          tabIndex={-1}
        >
          {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events */}
          <div
            className={`bottom-menu-content ${shouldUseLiquidGlass ? 'liquid-glass-chrome' : ''} ${isMenuClosing ? 'closing' : ''}`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="menu-header">
              <div className="menu-header-title-group">
                <h2 id="menu-header">Menu</h2>
              </div>
              <div className="menu-header-actions">
                <div className="menu-top-right-controls">
                  <ThemeToggle isDarkMode={isDarkMode} toggleTheme={toggleTheme} />

                  {shouldShowSnowButton() && typeof toggleSnow === 'function' && (
                    <button
                      onClick={() => {
                        triggerNavHaptic();
                        toggleSnow();
                      }}
                      className="snow-toggle"
                      aria-label={showSnow ? 'Désactiver la neige' : 'Activer la neige'}
                    >
                      {showSnow ? <Snowflake size={18} /> : <Sun size={18} />}
                    </button>
                  )}

                  {typeof changeLanguage === 'function' && typeof setIsLanguageMenuOpen === 'function' && (
                    <div className="language-selector">
                      <button
                        className="language-toggle"
                        onClick={() => setIsLanguageMenuOpen(!isLanguageMenuOpen)}
                        aria-label={t('changeLanguage')}
                      >
                        <picture>
                          <source srcSet={`/images/webp/${effectiveLanguage}-flag.webp`} type="image/webp" />
                          <img
                            src={`/images/${effectiveLanguage}-flag.png`}
                            alt={t(`languages.${effectiveLanguage}`)}
                            width="28"
                            height="19"
                          />
                        </picture>
                      </button>

                      {isLanguageMenuOpen && (
                        <div className="language-dropdown">
                          {['fr', 'en', 'es', 'it', 'de'].map((lang) => (
                            <button key={lang} onClick={() => changeLanguage(lang)}>
                              <picture>
                                <source srcSet={`/images/webp/${lang}-flag.webp`} type="image/webp" />
                                <img
                                  src={`/images/${lang}-flag.png`}
                                  alt={t(`languages.${lang}`)}
                                  width="28"
                                  height="19"
                                />
                              </picture>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {showIOSRefresh && (
                    <button
                      type="button"
                      className="ios-pwa-refresh-button"
                      onClick={() => window.location.reload()}
                      aria-label={t('common.refresh', 'Rafraîchir la page')}
                      title={t('common.refresh', 'Rafraîchir la page')}
                    >
                      ↻
                    </button>
                  )}
                </div>

                {/* Boutons de contrôle du mode réorganisation */}
                {!isReorderMode && hasCustomOrder(selectedContinent) && (
                  <button
                    onClick={handleResetOrder}
                    className="reset-order-btn-mobile icon-only"
                    title={t('menu.resetOrder')}
                    aria-label={t('menu.resetOrder')}
                  >
                    ↺
                    <span className="tooltip-mobile">{t('menu.resetOrder')}</span>
                  </button>
                )}
                <button onClick={closeMenu} className="close-menu-btn" aria-label={t('navigation.close')}>✕</button>
              </div>
            </div>

            <div className="continent-tabs">
              <button
                className={`continent-tab ${selectedContinent === 'europe' ? 'active' : ''}`}
                onClick={() => handleContinentSelect('europe')}
              >
                {t('continents.europe')}
              </button>
              <button
                className={`continent-tab ${selectedContinent === 'northAmerica' ? 'active' : ''}`}
                onClick={() => handleContinentSelect('northAmerica')}
              >
                {t('continents.northAmerica')}
              </button>
              <button
                className={`continent-tab ${selectedContinent === 'asia' ? 'active' : ''}`}
                onClick={() => handleContinentSelect('asia')}
              >
                {t('continents.asia')}
              </button>
              <div className="favorites-tab-group">
                <button
                  className={`continent-tab ${selectedContinent === 'favorites' ? 'active' : ''} ${!hasFavoriteParks ? 'disabled' : ''}`}
                  onClick={() => hasFavoriteParks && handleContinentSelect('favorites')}
                  disabled={!hasFavoriteParks}
                >
                  {t('continents.favorites')}
                </button>
                {!isReorderMode && (
                  <button
                    onClick={() => {
                      triggerNavHaptic();
                      handleEnterReorderMode();
                    }}
                    className="favorites-edit-btn"
                    title={t('menu.reorderMode')}
                    aria-label={t('menu.reorderMode')}
                  >
                    <Pencil size={16} />
                  </button>
                )}
              </div>
            </div>

            {isReorderMode && (
              <div className="reorder-mode-info-mobile">
                <div className="reorder-mode-header-mobile">
                  <span className="reorder-mode-title-mobile">{t('menu.reorderModeActive')}</span>
                  <p className="reorder-mode-text-mobile">{t('menu.reorderInfo')}</p>
                </div>
                <div className="reorder-actions-mobile">
                  <button
                    onClick={handleSaveOrder}
                    className="save-order-btn-mobile"
                  >
                    ✓ {t('menu.saveOrder')}
                  </button>
                  <button
                    onClick={handleCancelReorder}
                    className="cancel-order-btn-mobile"
                  >
                    ✕ {t('menu.cancelOrder')}
                  </button>
                </div>
              </div>
            )}

            <div className={`parks-list ${isReorderMode ? 'reorder-mode' : ''}`}>
              {getFilteredParks(selectedContinent).map((park, index) => (
                <div
                  key={park.id}
                  className={`park-item ${location.pathname === park.path ? 'active' : ''} ${isReorderMode ? 'reorder-active' : ''}`}
                >
                  {isReorderMode && (
                    <div className="reorder-buttons-mobile">
                      <div className="reorder-arrows-mobile">
                        <button
                          onClick={() => handleMoveParkUp(index)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              handleMoveParkUp(index);
                            }
                          }}
                          disabled={index === 0}
                          className="move-btn-mobile move-up"
                          aria-label={t('menu.moveUp')}
                          title={t('menu.moveUp')}
                        >
                          <ArrowUp size={16} />
                        </button>
                        <button
                          onClick={() => handleMoveParkDown(index)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              handleMoveParkDown(index);
                            }
                          }}
                          disabled={index === getFilteredParks(selectedContinent).length - 1}
                          className="move-btn-mobile move-down"
                          aria-label={t('menu.moveDown')}
                          title={t('menu.moveDown')}
                        >
                          <ArrowDown size={16} />
                        </button>
                      </div>
                      {selectedContinent === 'favorites' && (
                        <button
                          type="button"
                          onClick={() => {
                            triggerNavHaptic();
                            const rawParkId = favoriteParkRawIdByNormalized.get(park.id) || park.id;
                            if (favoriteParkIdsOnlySet.has(park.id)) {
                              toggleParkFavorite(rawParkId);
                            }
                            excludeFavoritePark(park.id);
                            setTempParks((prev) => prev.filter((p) => p.id !== park.id));
                          }}
                          className="remove-favorite-park-btn"
                          aria-label={t('menu.removeParkFavorite')}
                          title={t('menu.removeParkFavorite')}
                        >
                          <Trash2 size={16} />
                        </button>
                      )}
                      {selectedContinent !== 'favorites' && (
                        <button
                          onClick={() => {
                            const rawParkId = favoriteParkRawIdByNormalized.get(park.id) || park.id;
                            toggleParkFavorite(rawParkId);
                          }}
                          className="park-favorite-btn"
                          aria-pressed={favoriteParkIdsSet.has(park.id)}
                          aria-label={favoriteParkIdsSet.has(park.id) ? t('menu.removeParkFavorite') : t('menu.addParkFavorite')}
                          title={favoriteParkIdsSet.has(park.id) ? t('menu.removeParkFavorite') : t('menu.addParkFavorite')}
                        >
                          <Star
                            className={`favorite-icon ${favoriteParkIdsSet.has(park.id) ? 'active' : ''}`}
                            fill={favoriteParkIdsSet.has(park.id) ? 'currentColor' : 'none'}
                          />
                        </button>
                      )}
                    </div>
                  )}
                  {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
                  <div
                    onClick={() => !isReorderMode && handleParkSelect(park)}
                    onKeyDown={(e) => {
                      if (!isReorderMode && (e.key === 'Enter' || e.key === ' ')) {
                        handleParkSelect(park);
                      }
                    }}
                    className="park-item-content"
                    style={{ cursor: isReorderMode ? 'default' : 'pointer' }}
                    role={!isReorderMode ? 'button' : undefined}
                    tabIndex={!isReorderMode ? 0 : undefined}
                    aria-label={!isReorderMode ? `Sélectionner ${park.name}` : undefined}
                  >
                    <div className="park-logo-mini">
                      <picture>
                        <source srcSet={park.logo.replace('.png', '.webp').replace('/logos/', '/logos/webp/')} type="image/webp" />
                        <source srcSet={park.logo} type="image/png" />
                        <img
                          src={park.logo}
                          alt=""
                          aria-hidden="true"
                          loading="lazy"
                        />
                      </picture>
                    </div>
                    <span className="park-name-with-indicator">
                      <span className="park-name">{park.name}</span>
                      {!isReorderMode && shouldShowCrowdPredictionIndicator && CROWD_PREDICTION_PARK_PATHS.has(park.path) && (
                        <span
                          className="park-crowd-indicator"
                          title="Prédictions d'affluence disponibles"
                          aria-label="Prédictions d'affluence disponibles"
                        >
                          <Users size={13} aria-hidden="true" />
                        </span>
                      )}
                    </span>
                    {!isReorderMode && (park.id === 'dlp' || park.id === 'wds' || park.id === 'ast') && (
                      <button
                        type="button"
                        className="park-pass-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          triggerNavHaptic();
                          closeMenu();
                          navigate(park.id === 'ast' ? '/asterix-pass' : '/my-pass');
                        }}
                        aria-label={t('navigation.annualPass')}
                        title={t('navigation.annualPass')}
                      >
                        {createButtonImage('Pass', t('navigation.annualPass'), isDarkMode)}
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>

            <div className="settings-section">
              <h3>{t('settings.accessibility.title')}</h3>
              <div className="settings-items">
                {/* Toggle pour activer/désactiver l'effet liquid glass (Chrome uniquement) */}
                {isChrome && (
                  <div className="setting-item">
                    <LiquidToggle
                      checked={liquidGlassEnabled}
                      onChange={toggleLiquidGlass}
                      label={t('settings.liquidGlass', 'Effet Liquid Glass')}
                      ariaLabel={t('settings.liquidGlass', 'Activer/Désactiver l\'effet liquid glass')}
                      isActive={activeToggle === 'liquidGlass'}
                      onMouseDown={() => handleToggleMouseDown('liquidGlass')}
                      onMouseUp={handleToggleMouseUp}
                      onTouchStart={() => handleToggleMouseDown('liquidGlass')}
                      onTouchEnd={handleToggleMouseUp}
                    />
                  </div>
                )}

                <div className="setting-item">
                  {shouldUseLiquidGlass ? (
                    <LiquidToggle
                      checked={!!isHighContrast}
                      onChange={toggleHighContrast}
                      label={t('settings.accessibility.highContrast', 'Contraste élevé')}
                      ariaLabel={t('settings.accessibility.highContrast', 'Activer le mode contraste élevé')}
                      isActive={activeToggle === 'highContrast'}
                      onMouseDown={() => handleToggleMouseDown('highContrast')}
                      onMouseUp={handleToggleMouseUp}
                      onTouchStart={() => handleToggleMouseDown('highContrast')}
                      onTouchEnd={handleToggleMouseUp}
                    />
                  ) : (
                    <label className="toggle-switch">
                      <span>{t('settings.accessibility.highContrast', 'Contraste élevé')}</span>
                      <input
                        type="checkbox"
                        checked={!!isHighContrast}
                        onChange={toggleHighContrast}
                        aria-label={t('settings.accessibility.highContrast', 'Activer le mode contraste élevé')}
                      />
                      <span className="toggle-slider"></span>
                    </label>
                  )}
                </div>

                <div className="setting-item">
                  {shouldUseLiquidGlass ? (
                    <LiquidToggle
                      checked={hideNavLabels}
                      onChange={toggleHideNavLabels}
                      label={t('settings.accessibility.hideNavLabels')}
                      ariaLabel={t('settings.accessibility.hideNavLabels')}
                      isActive={activeToggle === 'hideNavLabels'}
                      onMouseDown={() => handleToggleMouseDown('hideNavLabels')}
                      onMouseUp={handleToggleMouseUp}
                      onTouchStart={() => handleToggleMouseDown('hideNavLabels')}
                      onTouchEnd={handleToggleMouseUp}
                    />
                  ) : (
                    <label className="toggle-switch">
                      <span>{t('settings.accessibility.hideNavLabels')}</span>
                      <input
                        type="checkbox"
                        checked={hideNavLabels}
                        onChange={toggleHideNavLabels}
                        aria-label={t('settings.accessibility.hideNavLabels')}
                      />
                      <span className="toggle-slider"></span>
                    </label>
                  )}
                </div>

                <div className="setting-item">
                  {shouldUseLiquidGlass ? (
                    <LiquidToggle
                      checked={showLocalTime}
                      onChange={toggleLocalTime}
                      label={t('settings.accessibility.showLocalTime', 'Afficher l\'heure locale')}
                      ariaLabel={t('settings.accessibility.showLocalTime', 'Afficher l\'heure locale')}
                      isActive={activeToggle === 'showLocalTime'}
                      onMouseDown={() => handleToggleMouseDown('showLocalTime')}
                      onMouseUp={handleToggleMouseUp}
                      onTouchStart={() => handleToggleMouseDown('showLocalTime')}
                      onTouchEnd={handleToggleMouseUp}
                    />
                  ) : (
                    <label className="toggle-switch">
                      <span>{t('settings.accessibility.showLocalTime', 'Afficher l\'heure locale')}</span>
                      <input
                        type="checkbox"
                        checked={showLocalTime}
                        onChange={toggleLocalTime}
                        aria-label={t('settings.accessibility.showLocalTime', 'Afficher l\'heure locale')}
                      />
                      <span className="toggle-slider"></span>
                    </label>
                  )}
                </div>


                <div className="setting-item parking-assistant-item">
                  <button
                    onClick={() => {
                      setShowParkingModal(true);
                      closeMenu();
                    }}
                    className="parking-assistant-btn"
                  >
                    <span className="parking-icon">🚗</span>
                    <span>{t('parking.title')}</span>
                  </button>
                </div>

              </div>
            </div>

            <div className="menu-links">
              <Link
                to="/about"
                className="menu-link"
                onClick={() => {
                  triggerNavHaptic();
                  closeMenu();
                }}
              >
                {t('legal.title')}
              </Link>
              <a
                href="https://lite.magicparktimes.com"
                className="menu-link lite-link"
                target="_blank"
                rel="noopener noreferrer"
                title={t('lite.title')}
                onClick={triggerNavHaptic}
              >
                {t('lite.title')}
              </a>
              <Link
                to="/support-us"
                className="menu-link support-link"
                onClick={() => {
                  triggerNavHaptic();
                  closeMenu();
                }}
              >
                {t('supportUs.menuTitle')}
              </Link>
            </div>
          </div>
        </div>
      )}

      {/* Modal météo */}
      <WeatherModal
        isOpen={showWeatherModal}
        onClose={closeWeatherModal}
        parkCoordinates={getParkCoordinates(location.pathname)}
        parkName={getParkCoordinates(location.pathname)?.name || t('navigation.park')}
        isClosing={isWeatherClosing}
      />

      {/* Modal Parking Assistant */}
      <ParkingAssistant
        isOpen={showParkingModal}
        onClose={() => setShowParkingModal(false)}
      />
    </>
  );
};

export default BottomNavigation;
